import { TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { html, unsafeStatic } from 'lit/static-html.js';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './ghs-heading.css.lit';
import { OutlineHeading } from '../../base/outline-heading/outline-heading';
import {
  AllowedHeadingLevels,
  AllowedHeadingStyles,
} from '../../base/outline-heading/config';
import { LightController } from '../../controllers/light-controller';

/**
 * The Heading component.
 * @element ghs-heading
 * @slot
 */
@customElement('ghs-heading')
export class GhsHeading extends OutlineHeading {
  static styles = [componentStyles];

  lightController = new LightController(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  /**
   * The heading level style to apply. Optional override to default styles for a given level
   */
  @property({ type: String, reflect: true, attribute: 'level-style' })
  levelStyle: AllowedHeadingStyles;

  /**
   * The tag to apply: h1 | h2 | h3 | h4 | h5 | h6
   */
  @property({ type: String, reflect: true, attribute: 'level' })
  level: AllowedHeadingLevels = undefined;

  @property({ type: Boolean, reflect: false, attribute: 'border' })
  border: boolean;

  @property({ type: Boolean, reflect: false, attribute: 'inherit-color' })
  inheritColor = false;

  firstUpdated() {
    super.firstUpdated();
    if (this.site !== 'BH' && !this.levelStyle) {
      this.levelStyle = 'bold';
    }

    if (this.border === undefined) {
      // Default border for H1 Bellin Site, use .border=${false} to hide border.
      this.border = this.site === 'BH' && this.level === 'h1';
    }
  }

  render(): TemplateResult {
    const classes = {
      'outline-text': true,
      [`outline-text--${this.levelSize}`]: this.levelSize,
      [`outline-heading--${this.level}`]: this.level || '',
      [`outline-font--${this.levelStyle}`]: this.levelStyle,
      'mobile': this.mobileController.isMobile,
      'border': this.border,
    };

    return this.site === 'BH' && this.level !== undefined
      ? html`<${unsafeStatic(this.level as string)} class=${classMap(classes)}>
        <slot></slot>
      </${unsafeStatic(this.level as string)}>`
      : html`
          <div class=${classMap(classes)}>
            <slot></slot>
          </div>
        `;
  }
}
